import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Activity,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Zap,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';

const AutonomyEvents = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [eventTypes, setEventTypes] = useState([]);
  
  const [filters, setFilters] = useState({
    event_type: searchParams.get('event_type') || '',
    entity_type: searchParams.get('entity_type') || '',
    processed: searchParams.get('processed') || '',
    skip: parseInt(searchParams.get('skip')) || 0,
    limit: 20
  });

  const fetchEventTypes = useCallback(async () => {
    try {
      const res = await api.get('/admin/autonomy/events/types');
      setEventTypes(res.data.event_types || []);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('skip', filters.skip);
      params.set('limit', filters.limit);
      if (filters.event_type) params.set('event_type', filters.event_type);
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.processed !== '') params.set('processed', filters.processed);
      
      const res = await api.get(`/admin/autonomy/events?${params.toString()}`);
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchEventTypes();
  }, [fetchEventTypes]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, skip: 0 };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.event_type) params.set('event_type', newFilters.event_type);
    if (newFilters.entity_type) params.set('entity_type', newFilters.entity_type);
    if (newFilters.processed !== '') params.set('processed', newFilters.processed);
    setSearchParams(params);
  };

  const handlePageChange = (direction) => {
    const newSkip = direction === 'next' 
      ? filters.skip + filters.limit 
      : Math.max(0, filters.skip - filters.limit);
    setFilters({ ...filters, skip: newSkip });
  };

  const eventTypeColorMap = {
    'case_created': 'bg-blue-100 text-blue-800',
    'case_updated': 'bg-indigo-100 text-indigo-800',
    'payment_received': 'bg-green-100 text-green-800',
    'payment_failed': 'bg-red-100 text-red-800',
    'maintenance_requested': 'bg-orange-100 text-orange-800',
    'inspection_scheduled': 'bg-purple-100 text-purple-800',
    'document_uploaded': 'bg-gray-100 text-gray-800',
    'tenant_communication': 'bg-cyan-100 text-cyan-800'
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="autonomy-events-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/autonomy" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">AI Events</h1>
                <p className="text-sm text-gray-500">System events processed by AI autonomy layer</p>
              </div>
            </div>
            <Button onClick={fetchEvents} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <select
                value={filters.event_type}
                onChange={(e) => handleFilterChange('event_type', e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="filter-event-type"
              >
                <option value="">All Event Types</option>
                {eventTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <Input
                placeholder="Entity Type..."
                value={filters.entity_type}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                className="w-40"
                data-testid="filter-entity-type"
              />

              <select
                value={filters.processed}
                onChange={(e) => handleFilterChange('processed', e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="filter-processed"
              >
                <option value="">All Status</option>
                <option value="true">Processed</option>
                <option value="false">Unprocessed</option>
              </select>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setFilters({ event_type: '', entity_type: '', processed: '', skip: 0, limit: 20 });
                  setSearchParams({});
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Events List */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Events ({total})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No events found</p>
              </div>
            ) : (
              <div className="divide-y">
                {events.map((event) => (
                  <div key={event.id} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`event-${event.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${event.processed ? 'bg-green-100' : 'bg-yellow-100'}`}>
                          {event.processed ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Activity className="w-4 h-4 text-yellow-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className={eventTypeColorMap[event.event_type] || 'bg-gray-100 text-gray-800'}>
                              {event.event_type}
                            </Badge>
                            <span className="text-sm text-gray-500">{event.entity_type}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            ID: {event.entity_id || 'N/A'}
                          </p>
                          {event.payload && (
                            <pre className="text-xs bg-gray-50 p-2 rounded mt-2 max-w-xl overflow-auto">
                              {JSON.stringify(event.payload, null, 2).slice(0, 200)}
                              {JSON.stringify(event.payload).length > 200 && '...'}
                            </pre>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">
                          {event.created_at ? new Date(event.created_at).toLocaleString() : '-'}
                        </p>
                        {event.actor_type && (
                          <p className="text-xs text-gray-500 mt-1">
                            by {event.actor_type}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Showing {filters.skip + 1} - {Math.min(filters.skip + filters.limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.skip === 0}
              onClick={() => handlePageChange('prev')}
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.skip + filters.limit >= total}
              onClick={() => handlePageChange('next')}
              data-testid="next-page"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AutonomyEvents;
